// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Truncate } from "@re-dev/react-truncate";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip from "@stoneage/MobiusOverrides/Tooltip";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: GridContainerProps;
    attributeTypeGridItem?: GridItemProps;
    attributeValueGridItem?: GridItemProps;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    itemContainer?: InjectableCss;
    attributeTypeSizer?: TypographyPresentationProps;
    attributeTypeColon?: TypographyPresentationProps;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        gap: 0,
    },
    attributeTypeGridItem: {
        width: [6, 6, 6, 4, 4],
        css: css`
            overflow: hidden;
        `,
    },
    attributeValueGridItem: {
        width: [6, 6, 6, 8, 8],
        css: css`
            overflow: hidden;
        `,
    },
    attributeTypeText: {
        weight: "bold",
    },
};

const ProductAttributes: FC<Props> = ({ product: { attributeTypes }, maximumNumberAttributeTypes, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const truncatedWithTooltip = (text: string) => (
        <Truncate
            lines={1}
            ellipsis={
                <Tooltip
                    closeOthersOnOpen={true}
                    triggerComponent={<span>...</span>}
                    text={text}
                    cssOverrides={{
                        tooltipWrapper: css`
                            margin-left: 0;
                        `,
                    }}
                />
            }
            preserveMarkup
        >
            {text}
        </Truncate>
    );

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) =>
        truncatedWithTooltip((attributeValues ?? []).map(a => a.valueDisplay).join(" | "));

    return (
        <StyledWrapper {...styles.container} data-test-selector="ProductAttributes">
            {limitedAttributeTypes.map(attributeType => {
                const label = attributeType.label || attributeType.name;

                return (
                    <StyledWrapper key={attributeType.id.toString()} {...styles.itemContainer}>
                        <StyledWrapper {...styles.attributeTypeGridItem}>
                            {/* in-flow, invisible: this is what gives the cell its width */}
                            <Typography {...styles.attributeTypeSizer} aria-hidden="true">
                                {label}
                            </Typography>
                            {/* out of flow: consumes the width, never contributes to it */}
                            <Typography
                                {...styles.attributeTypeText}
                                data-test-selector={`attributeType${attributeType.id}`}
                            >
                                {truncatedWithTooltip(label)}
                            </Typography>
                        </StyledWrapper>

                        <Typography {...styles.attributeTypeColon} aria-hidden="true">
                            :
                        </Typography>

                        <StyledWrapper
                            {...styles.attributeValueGridItem}
                            data-test-selector={`attributeValuesFor${attributeType.id}`}
                            translate="no"
                        >
                            <Typography {...styles.attributeValueText}>
                                {displayAttributeValues(attributeType.attributeValues)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

export default withProduct(ProductAttributes);
